import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "src/App";

const getActivities = (userId) => {
  return axios
    .get(`${nuclearn_api_url}/products/observation-program/activity`, {
      params: {
        user_id: userId,
      },
    })
    .then((response) => response.data);
};

const getActivityById = (activityId) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/activity/${activityId}/details`,
    )
    .then((response) => response.data);
};

export const useGetActivityDetails = (activityId) => {
  return useQuery(
    ["activityDetails", activityId],
    () => getActivityById(activityId),
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error fetching activity details:", error);
      },
      enabled: Boolean(activityId),
    },
  );
};

const getObservationSystemPrompt = (activityId) => {
  //TODO: Update URL
  return axios
    .get(`${nuclearn_api_url}/products/observation-program/chat/context`, {
      params: {
        activity_id: activityId,
      },
    })
    .then((response) => response.data);
};

export const useGetObservationSystemPrompt = (activityId) => {
  return useQuery({
    queryKey: ["obsSystemPrompt", activityId],
    queryFn: () => getObservationSystemPrompt(activityId),
    onSuccess: () => {},
    onError: (error) => {
      console.error("Error fetching activity details:", error);
    },
    enabled: Boolean(activityId),
  });
};

export const useGetActivities = (userId) => {
  // This will pull all the activities.
  return useQuery(["activities", userId], () => getActivities(), {
    onSuccess: () => {},
    onError: (error) => {
      console.error("Error fetching activities:", error);
    },
  });
};

const createActivity = ({ activityName, sdrData, dataset_fk }) => {
  return axios
    .post(`${nuclearn_api_url}/products/observation-program/activity`, {
      activity_name: activityName,
      data: sdrData,
      dataset_fk,
    })
    .then((response) => response.data);
};

export const useCreateActivity = () => {
  const queryClient = useQueryClient();
  return useMutation(createActivity, {
    onSuccess: () => {
      queryClient.invalidateQueries("activities");
      queryClient.invalidateQueries("assignedActivities");
      queryClient.invalidateQueries("suggestedActivities");
    },
    onError: () => {
      queryClient.invalidateQueries("activities");
      queryClient.invalidateQueries("assignedActivities");
      queryClient.invalidateQueries("suggestedActivities");
    },
  });
};

export const useGetActivitiesAssignedToUser = (userId) => {
  // this will grab the activities assigned to some user id.
  return useQuery("assignedActivities", () => getActivities(userId), {
    onSuccess: () => {},
    onError: (error) => {
      console.error("Error fetching activities:", error);
    },
  });
};

const getSuggestedActivities = (userId) => {
  // Grab suggested activities for this user id( likely me )
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/suggested-activity`,
      {
        params: {
          user_id: userId,
        },
      },
    )
    .then((response) => response.data);
};

export const useGetSuggestedActivities = (userId) => {
  // Grab suggested activities for this user id( likely me )

  return useQuery(
    ["suggestedActivities", userId],
    () => getSuggestedActivities(userId),
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error fetching suggested activities:", error);
      },
    },
  );
};

const getCriteria = () => {
  // Grab suggested activities for this user id( likely me )
  return axios
    .get(`${nuclearn_api_url}/products/observation-program/criteria`)
    .then((response) => response.data);
};

export const useGetCriteria = () => {
  // Grab suggested activities for this user id( likely me )

  return useQuery(["criteria"], () => getCriteria(), {
    onSuccess: () => {},
    onError: (error) => {
      console.error("Error fetching suggested activities:", error);
    },
  });
};

const getActivityCriteria = (activityId) => {
  /*
   * Grab suggested activities for this activity id (likely me)
   * /activity/{activity_id}/criteria
   */
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/activity/${activityId}/criteria`,
    )
    .then((response) => response.data);
};

export const useGetActivityCriteria = (activityId) => {
  return useQuery(
    ["criteria", activityId],
    () => getActivityCriteria(activityId),
    {
      // eslint-disable-next-line no-unused-vars
      onSuccess: (data) => {
        // console.log("Got activity criteria:", data);
      },
      // eslint-disable-next-line no-unused-vars
      onError: (error) => {
        // console.error("Error fetching suggested activities:", error);
      },
      retry: 1,
      enabled: Boolean(activityId),
    },
  );
};

const getAttributes = () => {
  /*
   * Grab suggested activities for this activity id (likely me)
   * /activity/{activity_id}/criteria
   */
  return axios
    .get(`${nuclearn_api_url}/products/observation-program/attributes`)
    .then((response) => response.data);
};

export const useGetAttributes = () => {
  return useQuery(["attributes"], () => getAttributes(), {
    retry: 1,
  });
};

const AssignUserToActivity = (activityId, userId) => {
  return axios
    .post(
      `${nuclearn_api_url}/products/observation-program/activity/assign-user/`,
      {
        activity_id: activityId,
        user_id: userId,
      },
    )
    .then((response) => response.data);
};

export const useAssignUserToActivity = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars) => {
      const [activityId, userId] = vars;
      return AssignUserToActivity(activityId, userId);
    },
    {
      onSuccess: () => {
        // Invalidate all relevant queries
        queryClient.invalidateQueries("activities");
        queryClient.invalidateQueries("assignedActivities");
        queryClient.invalidateQueries("suggestedActivities");
        // Invalidate paginated queries
        queryClient.invalidateQueries(["userActivities"]);
        queryClient.invalidateQueries(["suggestedActivities"]);
        // Invalidate count queries
        queryClient.invalidateQueries(["userActivitiesCount"]);
        queryClient.invalidateQueries(["suggestedActivitiesCount"]);
      },
      onError: (error) => {
        console.error("Error assigning user to activity.", error.response);
      },
    },
  );
};

const unassignUserToActivity = (activityId, userId) => {
  const url = `${nuclearn_api_url}/products/observation-program/activity/unassign-user/?activity_id=${activityId}&user_id=${userId}`;

  return axios
    .delete(url)
    .then((response) => response.data)
    .catch((error) => console.error(error));
};

export const useUnassignUserToActivity = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars) => {
      const [activityId, userId] = vars;
      return unassignUserToActivity(activityId, userId);
    },
    {
      onSuccess: () => {
        // Invalidate all relevant queries
        queryClient.invalidateQueries("activities");
        queryClient.invalidateQueries("assignedActivities");
        queryClient.invalidateQueries("suggestedActivities");
        // Invalidate paginated queries
        queryClient.invalidateQueries(["userActivities"]);
        queryClient.invalidateQueries(["suggestedActivities"]);
        // Invalidate count queries
        queryClient.invalidateQueries(["userActivitiesCount"]);
        queryClient.invalidateQueries(["suggestedActivitiesCount"]);
      },
      onError: (error) => {
        console.error("Error unassigning user to activity.", error.response);
      },
    },
  );
};

const searchObservations = (searchTerm) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/search-observations`,
      {
        params: { search_phrase: searchTerm },
      },
    )
    .then((response) => response.data);
};

// Custom hook using useMutation for searching observations
const useSearchObservations = () => {
  return useMutation(searchObservations, {
    onSuccess: () => {
      // Handle success state
    },
    onError: (error) => {
      // Handle error state
      console.error("Search error:", error.response);
    },
  });
};

const searchActivities = (searchTerm) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/activity/search?activity_name=${searchTerm}`,
    )
    .then((response) => response.data);
};

// Custom hook using useMutation for searching observations
export const useSearchActivities = () => {
  return useMutation(searchActivities, {
    onSuccess: () => {
      /*
       * Handle success state
       * console.log("Search results:", data);
       */
    },
    onError: () => {
      /*
       * Handle error state
       * console.error("Search error:", error.response);
       */
    },
  });
};

const getObservations = () => {
  return axios
    .get(`${nuclearn_api_url}/products/observation-program/get-observations`)
    .then((response) => response.data);
};
const useGetObservations = () => {
  return useQuery("observations", getObservations, {
    onSuccess: () => {
      // console.log("Observations:", data);
    },
    onError: () => {
      // console.error("Error fetching observations:", error);
    },
  });
};

const getObservationSuggestions = () => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/get-observation-suggestions`,
    )
    .then((response) => response.data);
};

const useGetObservationSuggestions = () => {
  return useQuery("observationSuggestions", getObservationSuggestions, {
    onSuccess: () => {
      // console.log("Observation Suggestions:", data);
    },
    onError: () => {
      // console.error("Error fetching observation suggestions:", error);
    },
  });
};

const getObservationRecents = () => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/get-recent-observations`,
    )
    .then((response) => response.data);
};

const useGetObservationRecents = () => {
  return useQuery("observationRecents", getObservationRecents, {
    onSuccess: () => {
      // console.log("Observation Recents:", data);
    },
    onError: (error) => {
      console.error("Error fetching observation recents:", error);
    },
  });
};

const getUserObservations = (observationId) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/get-user-observations/${observationId}`,
    )
    .then((response) => response.data);
};

const useGetUserObservations = (observationId) => {
  return useQuery(
    ["userObservations", observationId],
    () => getUserObservations(observationId),
    {
      // Optional: configure how the query behaves, e.g., refetch intervals, stale time, etc.
      onSuccess: () => {
        // console.log("Fetched user observations successfully:", data);
      },
      onError: (error) => {
        console.error("Error fetching user observations:", error);
      },
      enabled: Boolean(observationId), // Only run this query if an observationId is provided
    },
  );
};

const createObservation = ({
  activityObserved,
  summary,
  description,
  attributes,
  caseId,
}) => {
  return axios.post(
    `${nuclearn_api_url}/products/observation-program/create-observation`,
    {
      activity_observed: activityObserved,
      summary,
      description,
      attributes,
      case_id: caseId,
    },
  );
};

const useCreateObservation = () => {
  const queryClient = useQueryClient();
  return useMutation(createObservation, {
    onSuccess: () => {
      queryClient.invalidateQueries("observations");
      queryClient.invalidateQueries("observationSuggestions");
      queryClient.invalidateQueries("observationRecents");
    },
    onError: (error) => {
      console.error("Error creating observation:", error.response);
    },
  });
};

const getUseAssignedActivitiesPageCount = (page_count) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/activity/user/page-count/${page_count}`,
    )
    .then((response) => {
      return response.data;
    });
};

export const useAssignedActivitiesPageCount = (page_count) => {
  return useQuery(
    ["userActivitiesCount", page_count], // Add page_count to query key to ensure uniqueness per page
    () => getUseAssignedActivitiesPageCount(page_count), // Wrap the call in an arrow function
    {
      onSuccess: () => {
        // console.log("userActivitiesCount:", data);
      },
      onError: (error) => {
        console.error("Error fetching userActivitiesCount:", error);
      },
    },
  );
};

const getUseSuggestedActivitiesPageCount = (page_count) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/activity/suggested/page-count/${page_count}`,
    )
    .then((response) => response.data);
};

export const useSuggestedActivitiesPageCount = (page_count) => {
  return useQuery(
    ["suggestedActivitiesCount", page_count], // Include page_count in the query key
    () => getUseSuggestedActivitiesPageCount(page_count), // Use an arrow function to delay execution
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error fetching suggestedActivitiesCount:", error);
      },
    },
  );
};

const fetchSuggestedActivities = (pageNumber, itemsPerPage) => {
  if (!pageNumber || !itemsPerPage) {
    return;
  }
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/activity/suggested/${pageNumber}/${itemsPerPage}`,
    )
    .then((response) => response.data);
};

export const useSuggestedActivities = (pageNumber, itemsPerPage) => {
  return useQuery(
    ["suggestedActivities", pageNumber, itemsPerPage], // Unique query key with pagination parameters
    () => fetchSuggestedActivities(pageNumber, itemsPerPage), // Fetching function
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error fetching suggested activities:", error);
      },
    },
  );
};

const fetchUserActivities = (pageNumber, itemsPerPage) => {
  return axios
    .get(
      `${nuclearn_api_url}/products/observation-program/activity/user/${pageNumber}/${itemsPerPage}`,
    )
    .then((response) => response.data);
};
export const useUserActivities = (pageNumber, itemsPerPage) => {
  return useQuery(
    ["userActivities", pageNumber, itemsPerPage], // Unique query key with pagination parameters
    () => fetchUserActivities(pageNumber, itemsPerPage), // Fetching function
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error("Error fetching user activities:", error);
      },
      enabled: Boolean(pageNumber), // Only run this query if a pageNumber is provided
    },
  );
};

/*
 * const createObservationChat = (userMessage) => {
 *   return axios.post(
 *     `${nuclearn_api_url}/products/observation-program/observation-chat`,
 *     {
 *       summary: {
 *         key1: "value1",
 *         key2: "value2",
 *       },
 *       activity_observed: "Turbine Fan Blade Failure",
 *       description:
 *         "The Turbine blade flew into the sidewall and separated from the main assembly",
 *       attributes: "Sample Attributes",
 *       case_id: 12345,
 *       id: 67890,
 *       create_date: "2024-02-22T00:00:00Z",
 *       chat: {
 *         message: userMessage,
 *         timestamp: "2024-02-22T12:34:56Z",
 *       },
 *     }
 *   );
 * };
 */

/*
 * export const useCreateObservationChat = () => {
 *   const queryClient = useQueryClient();
 *   return useMutation(createObservationChat, {
 *     onSuccess: () => {
 *       queryClient.invalidateQueries("observations");
 *       queryClient.invalidateQueries("observationSuggestions");
 *       queryClient.invalidateQueries("observationRecents");
 *     },
 *     onError: (error) => {
 *       console.error("Error creating observation chat:", error.response);
 *     },
 *   });
 * };
 */

const createObservationChat = (userMessage) => {
  return axios.post(
    `${nuclearn_api_url}/products/observation-program/observation-chat`,
    {
      summary: {
        key1: "value1",
        key2: "value2",
      },
      activity_observed: "Turbine Fan Blade Failure",
      description:
        "The Turbine blade flew into the sidewall and separated from the main assembly",
      attributes: "Sample Attributes",
      case_id: 12345,
      id: 67890,
      create_date: "2024-02-22T00:00:00Z",
      chat: {
        message: userMessage,
        timestamp: "2024-02-22T12:34:56Z",
      },
    },
  );
};

const useCreateObservationChat = () => {
  const queryClient = useQueryClient();
  return useMutation(createObservationChat, {
    onSuccess: () => {
      queryClient.invalidateQueries("observations");
      queryClient.invalidateQueries("observationSuggestions");
      queryClient.invalidateQueries("observationRecents");
    },
    onError: (error) => {
      console.error("Error creating observation chat:", error.response);
    },
  });
};
